// IMPORTANT: keep this map in sync with the backend
// `Peptide Order - Update Status_PeptideUpdateStatusScript.js`
// `allowedTransitions` map. Drift causes silent UX bugs where the kanban
// lets a drag through that the server then rejects.
const TRANSITIONS = {
  // New pay-at-submit state model — patient pays at checkout, backend holds auth
  // provider approval triggers capture; deny/cancel triggers auth release
  payment_authorized: ["payment_capturing", "denied", "cancelled", "payment_failed", "awaiting_restock"],
  payment_capturing: ["confirmed", "payment_failed", "awaiting_restock"],
  payment_failed: ["payment_authorized", "cancelled"],
  confirmed: ["readyForPickup", "shipped", "cancelled", "awaiting_restock"],
  // Slice C — awaiting_restock is reachable from create flows (any line
  // back-ordered) or from a stock decrement that invalidates reservations.
  // Allocator promotion (Slice D) flips it back to confirmed when the last
  // back-ordered line gets allocated; manufacturer/provider can also cancel
  // an order stuck in this state.
  awaiting_restock: ["confirmed", "cancelled"],
  readyForPickup: ["delivered"],
  shipped: ["delivered"],
  delivered: [],
  cancelled: [],
  denied: [],

  // Legacy states — kept for kanban/provider surfaces that haven't migrated yet
  draft: ["payment_pending", "cancelled", "awaiting_restock"],
  payment_pending: ["confirmed", "cancelled", "awaiting_restock"],
};

// Patient-allowed transitions — patients can cancel draft (legacy) or payment_authorized orders
const PATIENT_TRANSITIONS = {
  draft: ["cancelled"], // legacy: old orders still in draft during migration
  payment_authorized: ["cancelled"], // pay-at-submit: cancel before provider acts
  // Slice C — an order stuck in awaiting_restock should still be patient-cancellable.
  awaiting_restock: ["cancelled"],
};

const MANUAL_PAYMENT_ADVANCE_TARGETS = new Set(["confirmed", "shipped", "readyForPickup"]);

// Payer classification is deliberately exact. New patient-link orders are
// stamped `payerType: "patient"` by the backend; legacy orders without the
// field and exact provider-paid orders retain their existing manual payment
// flow. Any other present value fails closed.
export const isPatientPayerOrder = (order) => order?.payerType === "patient";

// Shared gate for every provider/manufacturer-side payment mutation. Split
// children inherit the parent's payment and patient-payer orders are paid only
// through their secure public link.
//
// `payerType: null` means "not patient-paid" — the SAME thing as absent — and
// must NOT fail closed. That is the backend's actual contract, not an
// assumption: `Peptide Order - Manufacturer - Create` persists
//   payerType: (source === "patient-intake" && referringProviderOrg && pricingQuoteId) ? "patient" : null
// so every provider-placed order carries an explicit null, and `Peptide Order -
// Get` returns toMap(false) unfiltered, nulls included. The backend's own
// consumer reads it the same way (`repCommission.js`: `payerType === "patient"`),
// and nothing anywhere emits the literal "provider". Failing closed on null
// would therefore disable card charge, Zelle upload, drag out of
// payment_pending, and Confirm/Deny Payment on every newly created provider
// order.
//
// Only patient orders are ever stamped, and they are stamped exactly "patient",
// so null can never mean "might be patient-paid". Any OTHER present value is a
// string we don't recognize and still fails closed.
export const canUseManualPeptidePaymentActions = (order) =>
  Boolean(order) && !order.parentOrderId && (order.payerType == null || order.payerType === "provider");

export const isManualPeptidePaymentAdvance = (order, targetStatus) =>
  order?.status === "payment_pending" && MANUAL_PAYMENT_ADVANCE_TARGETS.has(targetStatus);

export function canMovePeptideToStatus({ order, targetStatus, isManufacturer, isPatient, manualAction }) {
  // Patients can only cancel draft orders
  if (isPatient) {
    const allowed = PATIENT_TRANSITIONS[order.status] || [];
    return allowed.includes(targetStatus);
  }
  // Providers cannot move orders between statuses — only manufacturers can confirm/advance.
  // Providers can only upload receipts (handled by canUploadPaymentReceipt).
  if (!isManufacturer) {
    return false;
  }
  if (isManualPeptidePaymentAdvance(order, targetStatus) && !canUseManualPeptidePaymentActions(order)) {
    return false;
  }
  const allowed = TRANSITIONS[order.status] || [];
  if (!allowed.includes(targetStatus)) return false;
  if (order.status === "confirmed") {
    if (targetStatus === "readyForPickup" && order.fulfillmentType !== "pickup") return false;
    if (targetStatus === "shipped" && order.fulfillmentType !== "shipped") return false;
  }
  return true;
}

export function canDragPeptideOrder({ order, isManufacturer, isPatient }) {
  if (order.isArchived) return false;
  if (isPatient) return false;
  if (order.status === "payment_pending" && !canUseManualPeptidePaymentActions(order)) return false;
  if (!isManufacturer) {
    return false;
  }
  const allowed = TRANSITIONS[order.status] || [];
  return allowed.length > 0;
}

export function canUploadPaymentReceipt({ order, isManufacturer, isPatient }) {
  if (isPatient) return false;
  return canUseManualPeptidePaymentActions(order) && !isManufacturer && order.status === "payment_pending";
}

// Archivable = removable from the board (recoverable via the "Only Archived"
// filter). Drafts, plus the terminal "bad" statuses that pile up in the Cancelled
// lane (cancelled / denied / payment_failed) — so a reviewed cancelled order can
// be cleared from the board.
const ARCHIVABLE_PEPTIDE_STATUSES = new Set(["draft", "cancelled", "denied", "payment_failed", "delivered"]);

export function canArchivePeptideOrder({ order }) {
  return !!order && ARCHIVABLE_PEPTIDE_STATUSES.has(order.status);
}

export function canPatientViewOrder({ order, patientId }) {
  if (!patientId || !order) return false;
  return order.patientId === patientId;
}

// Canonical status registry — single source of truth for the patient-facing
// tab bucket, card headline, fulfillment-tracker step index, and order-list
// sort ordinal, in addition to the existing kanban label/color fields. New
// statuses MUST fill all four of patientBucket / headline / trackerStep /
// sortOrdinal (headline may be omitted only for statuses that are
// special-cased ahead of the HEADLINES lookup, e.g. "delivered" — see
// orderHeadline.js). sortOrdinal drives sortPeptideOrders' "status" field
// (peptideHelpers.js) — tracked stages reuse their trackerStep value; the
// rest are placed in a fixed relative order after them (see
// docs/superpowers/specs/2026-07-08-peptide-order-list-sorting-design.md §3).
export const PEPTIDE_STATUSES = {
  // Legacy states — retained as aliases for backwards-compat with provider/kanban
  // surfaces that haven't migrated yet. New patient code paths use the
  // payment_authorized / denied / payment_failed names.
  draft: {
    label: "Draft",
    labelKey: "peptides.kanban.draft",
    color: "orange",
    patientBucket: "all",
    headline: { key: "peptides.orders.headline.draft", def: "Draft — not yet processed", accent: "default" },
    trackerStep: undefined,
    sortOrdinal: 6,
  },
  payment_pending: {
    label: "Order Placed",
    labelKey: "peptides.kanban.orderPlaced",
    color: "light-blue",
    patientBucket: "active",
    // Legacy alias — same headline copy as payment_authorized.
    headline: { key: "peptides.orders.headline.awaitingApproval", def: "Awaiting provider approval", accent: "default" },
    trackerStep: undefined,
    sortOrdinal: 4,
  },

  // New state model (T1 redesign)
  payment_authorized: {
    label: "Order placed",
    labelKey: "peptides.orderStatus.payment_authorized",
    color: "orange",
    patientBucket: "active",
    headline: { key: "peptides.orders.headline.awaitingApproval", def: "Awaiting provider approval", accent: "default" },
    trackerStep: 0,
    sortOrdinal: 0,
  },
  payment_capturing: {
    label: "Approving",
    labelKey: "peptides.orderStatus.payment_capturing",
    color: "light-blue",
    patientBucket: "active",
    headline: { key: "peptides.orders.headline.approving", def: "Approving your order", accent: "default" },
    trackerStep: 1,
    sortOrdinal: 1,
  },
  confirmed: {
    label: "Approved",
    labelKey: "peptides.orderStatus.confirmed",
    color: "blue",
    patientBucket: "active",
    headline: { key: "peptides.orders.headline.preparing", def: "Approved — preparing your order", accent: "default" },
    trackerStep: 1,
    sortOrdinal: 1,
  },
  readyForPickup: {
    label: "Pickup",
    labelKey: "peptides.kanban.readyForPickup",
    color: "green",
    patientBucket: "active",
    headline: { key: "peptides.orders.headline.readyForPickup", def: "Pickup", accent: "green" },
    trackerStep: 2,
    sortOrdinal: 2,
  },
  shipped: {
    label: "Shipped",
    labelKey: "peptides.orderStatus.shipped",
    color: "green",
    patientBucket: "active",
    headline: { key: "peptides.orders.headline.onItsWay", def: "On its way", accent: "green" },
    trackerStep: 2,
    sortOrdinal: 2,
  },
  delivered: {
    label: "Delivered",
    labelKey: "peptides.orderStatus.delivered",
    color: "gray",
    patientBucket: "delivered",
    // getOrderHeadline special-cases "delivered" before consulting this
    // registry (it needs deliveredAt/lastStateChangeAt to compute relative
    // day text) — no headline entry needed here.
    headline: undefined,
    trackerStep: 3,
    sortOrdinal: 3,
  },
  cancelled: {
    label: "Cancelled",
    labelKey: "peptides.orderStatus.cancelled",
    color: "gray",
    patientBucket: "canceled",
    headline: { key: "peptides.orders.headline.canceled", def: "Canceled", accent: "muted" },
    trackerStep: undefined,
    sortOrdinal: 9,
  },
  denied: {
    label: "Denied",
    labelKey: "peptides.orderStatus.denied",
    color: "red",
    patientBucket: "canceled",
    headline: { key: "peptides.orders.headline.denied", def: "Not approved by your provider", accent: "red" },
    trackerStep: undefined,
    sortOrdinal: 8,
  },
  payment_failed: {
    label: "Payment issue",
    labelKey: "peptides.orderStatus.payment_failed",
    color: "yellow",
    patientBucket: "canceled",
    headline: { key: "peptides.orders.headline.paymentIssue", def: "Payment issue — action needed", accent: "red" },
    trackerStep: undefined,
    sortOrdinal: 7,
  },
  awaiting_restock: {
    label: "Awaiting restock",
    labelKey: "peptides.orderStatus.awaiting_restock",
    color: "yellow",
    patientBucket: "all",
    headline: { key: "peptides.orders.headline.awaitingRestock", def: "Waiting on restock", accent: "default" },
    trackerStep: undefined,
    sortOrdinal: 5,
  },
};
